import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { productsAPI, ordersAPI, subscriptionsAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import {
  DollarSign,
  ShoppingCart,
  RefreshCw,
  CheckCircle,
  TrendingUp,
  Clock,
  Zap,
  Bell,
  Wifi,
} from 'lucide-react';

interface DashboardStats {
  totalRevenue: number;
  totalOrders: number;
  subscriptions: number;
  deliveredToday: number;
}

interface Activity {
  id: string;
  type: string;
  amount: number;
  time: string;
}

interface DealProduct {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  discount: number;
  image: string;
  endsIn: number;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    totalOrders: 0,
    subscriptions: 1,
    deliveredToday: 0,
  });
  const [activities, setActivities] = useState<Activity[]>([]);
  const [dealProduct, setDealProduct] = useState<DealProduct | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { addToCart } = useCart();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      if (parsedUser.role === 'delivery') {
        navigate('/delivery-agent');
        return;
      }
    }

    fetchDashboardData();

    // Update time every second for deal countdown
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [ordersRes, subsRes] = await Promise.all([
        ordersAPI.getStats().catch(() => ({ data: { totalOrders: 0, totalRevenue: 0 } })),
        subscriptionsAPI.getStats().catch(() => ({ data: { active: 1 } })),
      ]);

      setStats({
        totalRevenue: ordersRes.data?.totalRevenue || 0,
        totalOrders: ordersRes.data?.totalOrders || 0,
        subscriptions: subsRes.data?.active || 0,
        deliveredToday: ordersRes.data?.deliveredToday || 0,
      });

      // Get recent orders for activities
      const recentOrders = await ordersAPI.getAll().catch(() => ({ data: [] }));
      const activitiesList = (recentOrders.data || [])
        .slice(0, 5)
        .map((order: any) => ({
          id: order._id || order.id,
          type: `Order ${order.status} `,
          amount: order.totalAmount || 0,
          time: new Date(order.createdAt || order.created_at).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
        }));
      setActivities(activitiesList);

      // Get featured/deal product from products API
      const productsRes = await productsAPI.getAll().catch(() => ({ data: [] }));
      const products = productsRes.data || [];
      if (products.length > 0) {
        const featuredProduct = products[0];
        setDealProduct({
          id: featuredProduct._id || featuredProduct.id,
          name: featuredProduct.name,
          price: featuredProduct.price || 0,
          originalPrice: Math.round((featuredProduct.price || 0) * 1.5),
          discount: 33,
          image: featuredProduct.imageUrl || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=300',
          endsIn: 86400,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')} `;
  };

  const handleAddDealToCart = () => {
    if (dealProduct) {
      addToCart({
        id: dealProduct.id,
        _id: dealProduct.id,
        name: dealProduct.name,
        price: dealProduct.price,
        stock: 100,
        isActive: true,
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10b981]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-2">
            🥬 Welcome back, {user?.name?.split(' ')[0] || 'User'}!
          </h1>
          <p className="text-gray-500 flex items-center gap-2 mt-1">
            Live dashboard updates
            <span className="flex items-center gap-1 text-[#10b981] text-sm">
              <Wifi size={14} />
              Connected
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>Updated: {currentTime.toLocaleTimeString()}</span>
          <button className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={18} />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-lg">
            <Bell size={18} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Revenue */}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <DollarSign size={20} />
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1">
              <TrendingUp size={12} /> +15%
            </span>
          </div>
          <p className="text-2xl lg:text-3xl font-bold">₹{stats.totalRevenue}</p>
          <p className="text-sm text-white/80">Total Revenue</p>
        </div>

        {/* Orders */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <ShoppingCart size={20} />
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">0 new</span>
          </div>
          <p className="text-2xl lg:text-3xl font-bold">{stats.totalOrders}</p>
          <p className="text-sm text-white/80">Total Orders</p>
        </div>

        {/* Subscriptions */}
        <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <RefreshCw size={20} />
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{stats.subscriptions} active</span>
          </div>
          <p className="text-2xl lg:text-3xl font-bold">{stats.subscriptions}</p>
          <p className="text-sm text-white/80">Subscriptions</p>
        </div>

        {/* Delivered */}
        <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle size={20} />
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></span>
              Live
            </span>
          </div>
          <p className="text-2xl lg:text-3xl font-bold">{stats.deliveredToday}</p>
          <p className="text-sm text-white/80">Delivered Today</p>
        </div>
      </div>

      {/* Live Activity & Deal of the Day */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Live Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <Zap className="text-yellow-500" size={20} />
              Live Activity
            </h2>
            <span className="flex items-center gap-1 text-sm text-[#10b981]">
              <span className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse"></span>
              Real-time
            </span>
          </div>

          <div className="space-y-3">
            {activities.length > 0 ? (
              activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ShoppingCart className="text-blue-600" size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{activity.type}</p>
                    <p className="text-sm text-gray-500">
                      <span className="text-[#10b981] font-semibold">₹{activity.amount}</span>
                      {' '}{activity.time}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No recent activity</p>
            )}
          </div>
        </div>

        {/* Deal of the Day - Only for customers and admins */}
        {dealProduct && user?.role !== 'delivery' && (
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="text-yellow-400" size={20} />
              <span className="font-semibold text-yellow-400">Deal of the Day</span>
            </div>

            <div className="flex gap-4">
              <img
                src={dealProduct.image}
                alt={dealProduct.name}
                className="w-24 h-24 object-cover rounded-xl"
              />
              <div className="flex-1">
                <h3 className="text-xl font-bold">{dealProduct.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-2xl font-bold text-[#10b981]">₹{dealProduct.price}</span>
                  <span className="text-gray-400 line-through">₹{dealProduct.originalPrice}</span>
                </div>
                <span className="inline-block mt-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {dealProduct.discount}% OFF
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 text-sm text-gray-400">
              <Clock size={16} />
              <span>Ends in: {formatCountdown(dealProduct.endsIn)}</span>
            </div>

            <button
              onClick={handleAddDealToCart}
              className="w-full mt-4 bg-[#10b981] hover:bg-[#059669] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <ShoppingCart size={18} />
              Add to Cart
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;